import type { ReactElement } from "react";
import { companionKind } from "odyssey-shared";
import { EmptyState, Glyph, Segmented } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { CharacterSnapshot, CharacterStore, CompanionView } from "./store.ts";

/**
 * The Companions tab (ADR-138's amendment of 2026-10-31, decision 8; ADR-142's amendment of the same date): a rail of
 * the character's companions while it holds more than one, and for the selected one its portrait with an out or home
 * badge, its name and kind, the "With me" and "At home" control, the bond (the tier word and pips for everyone; the
 * exact bar only with Animal Lore, the author's bonding ruling of 2026-10-31) and today's feed state, and the cloak slot for
 * a kind that wears one. The chrome names no species; the kind's name, portrait and lines come from `COMPANION_KINDS`.
 * The control shows the server's state and never leads it.
 */

/**
 * Where a companion's portrait art is found: its first frame's url and size, or null for none yet (a silhouette).
 */
export type PortraitOf = (portrait: string) => { readonly url: string; readonly width: number; readonly height: number } | null;

/**
 * The portrait box with the out or home badge.
 *
 * @param props The companion and the portrait source.
 * @returns The box.
 */
function Portrait({ companion, portraitOf }: { readonly companion: CompanionView; readonly portraitOf: PortraitOf }): ReactElement {
  const kind = companionKind(companion.kind);
  const art = kind === null ? null : portraitOf(kind.portrait);
  return (
    <div className="ot-companion__portrait" data-companion="portrait">
      {art === null ? <Glyph name="user" /> : <span className="ot-companion__art" style={{ backgroundImage: `url(${art.url})` }} />}
      <span className={companion.out ? "ot-companion__badge is-out" : "ot-companion__badge"} role="img" aria-label={companion.out ? say("character.companions.outBadge") : say("character.companions.homeBadge")}>
        <Glyph name={companion.out ? "eye" : "eye-closed"} />
      </span>
    </div>
  );
}

/**
 * The selected companion's page.
 *
 * @param props The companion, the snapshot, the store and the portrait source.
 * @returns The page.
 */
function CompanionDetail({ companion, snapshot, store, portraitOf }: { readonly companion: CompanionView; readonly snapshot: CharacterSnapshot; readonly store: CharacterStore; readonly portraitOf: PortraitOf }): ReactElement {
  const kind = companionKind(companion.kind);
  const disabled = snapshot.frozen || snapshot.asking !== null;
  return (
    <div className="ot-companion" data-companion="detail">
      <div className="ot-companion__top">
        <Portrait companion={companion} portraitOf={portraitOf} />
        <div className="ot-companion__who">
          <span className="ot-companion__name">{companion.name}</span>
          <span className="ot-companion__kind">{kind === null ? "" : say(kind.nameKey as "character.companions.kinds.aethling")}</span>
          <Segmented
            label={companion.name}
            options={[
              { value: "out", label: say("character.companions.withMe"), disabled },
              { value: "home", label: say("character.companions.atHome"), disabled },
            ]}
            value={companion.out ? "out" : "home"}
            onChange={(value) => store.setOut(value === "out")}
          />
          {snapshot.frozen ? <span className="ot-companion__why">{say("character.companions.frozen")}</span> : null}
        </div>
      </div>
      {companion.bond === null ? null : (
        <div className="ot-companion__line" data-companion="bond">
          <span className="ot-companion__label">{say("character.companions.bond")}</span>
          <span className="ot-companion__tier">{companion.bond.tier}</span>
          {companion.bond.exact === null ? (
            <span className="ot-companion__pips" role="img" aria-label={say("character.companions.pips", { at: String(companion.bond.pips.at + 1), of: String(companion.bond.pips.of) })}>
              {Array.from({ length: companion.bond.pips.of }, (_, index) => (
                <span key={index} className={index <= (companion.bond?.pips.at ?? -1) ? "ot-companion__pip is-lit" : "ot-companion__pip"} />
              ))}
            </span>
          ) : (
            <span className="ot-companion__bar" role="img" aria-label={say("character.companions.lore")}>
              <span className="ot-companion__fill" style={{ width: `${Math.round(companion.bond.exact * 100)}%` }} />
            </span>
          )}
          <span className="ot-companion__next">{companion.bond.next === null ? "" : say("character.companions.next", { tier: companion.bond.next })}</span>
        </div>
      )}
      {companion.fed === null || kind === null ? null : (
        <div className="ot-companion__line" data-companion="today">
          <span className="ot-companion__label">{say("character.companions.today")}</span>
          <span className={companion.fed ? "ot-companion__fed" : "ot-companion__hungry"}>{say((companion.fed ? kind.fedKey : kind.hungryKey) as "character.companions.words.aethling.fed")}</span>
        </div>
      )}
      {kind?.cloak === true ? (
        <div className="ot-companion__cloak" data-companion="cloak">
          <span className="ot-companion__slot" aria-hidden="true">
            <Glyph name="lock" />
          </span>
          <span className="ot-companion__cloak-words">
            <span className="ot-companion__cloak-title">{say("character.companions.cloak")}</span>
            <span className="ot-companion__cloak-empty">{say("character.companions.cloakEmpty")}</span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The Companions tab's page.
 *
 * @param props The snapshot, the store and the portrait source.
 * @returns The page.
 */
export function CompanionsPage({ snapshot, store, portraitOf }: { readonly snapshot: CharacterSnapshot; readonly store: CharacterStore; readonly portraitOf: PortraitOf }): ReactElement {
  const selected = snapshot.companions.find((entry) => entry.id === snapshot.selected) ?? null;
  if (selected === null) {
    return (
      <div className="ot-companions ot-companions--none" data-companion="none">
        <EmptyState glyph="users" title={say("character.companions.none")}>
          {say("character.companions.noneLine")}
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="ot-companions" data-companion="page">
      {snapshot.companions.length > 1 ? (
        <ul className="ot-companions__rail" aria-label={say("character.companions.list")}>
          {snapshot.companions.map((entry) => (
            <li key={entry.id}>
              <button type="button" className={entry.id === selected.id ? "ot-companions__pick is-selected" : "ot-companions__pick"} aria-pressed={entry.id === selected.id} onClick={() => store.select(entry.id)}>
                <Glyph name={entry.out ? "eye-small" : "user-small"} />
                {entry.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <CompanionDetail companion={selected} snapshot={snapshot} store={store} portraitOf={portraitOf} />
    </div>
  );
}
